import React, { useState } from 'react';
import SEOScore from './SEOScore';
import './DuplicateRedirects.css';

const DuplicateRedirects = ({ duplicates, stats }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [minCount, setMinCount] = useState(2);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const filteredDuplicates = duplicates.filter(duplicate => {
    const matchesSearch = !searchTerm || 
      duplicate.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
      duplicate.sources.some(source => 
        source.source.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesCount = duplicate.count >= minCount;
    
    return matchesSearch && matchesCount;
  });

  const toggleGroup = (index) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    const allIndices = new Set(filteredDuplicates.map((_, index) => index));
    setExpandedGroups(allIndices);
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '301': return '#28a745';
      case '302': return '#ffc107';
      case '404': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div className="duplicate-redirects">
      <div className="duplicate-controls">
        <div className="duplicate-stats">
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-value">{stats.totalDuplicateTargets}</span>
              <span className="stat-label">Duplicate Targets</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.totalRedirectsInDuplicates}</span>
              <span className="stat-label">Total Redirects</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.maxDuplicatesForTarget}</span>
              <span className="stat-label">Max Per Target</span>
            </div>
          </div>
        </div>
        
        <div className="duplicate-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search targets or sources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="count-filter">
            <label htmlFor="minCount">Min count:</label>
            <select
              id="minCount"
              value={minCount}
              onChange={(e) => setMinCount(parseInt(e.target.value))}
              className="count-select"
            >
              <option value={2}>2+</option>
              <option value={3}>3+</option>
              <option value={5}>5+</option>
              <option value={10}>10+</option>
            </select>
          </div>
          
          <div className="expand-buttons">
            <button onClick={expandAll} className="expand-btn">
              Expand All
            </button>
            <button onClick={collapseAll} className="expand-btn">
              Collapse All
            </button>
          </div>
        </div>
      </div>

      <div className="duplicates-container">
        {filteredDuplicates.length === 0 ? (
          <div className="no-results">
            {searchTerm || minCount > 2 
              ? 'No duplicates match your filters' 
              : 'No duplicate redirects found'
            }
          </div>
        ) : (
          <div className="duplicate-groups">
            {filteredDuplicates.map((duplicate, index) => (
              <div key={index} className="duplicate-group">
                <div 
                  className="group-header"
                  onClick={() => toggleGroup(index)}
                >
                  <div className="group-info">
                    <span className={`expand-icon ${expandedGroups.has(index) ? 'expanded' : ''}`}>
                      ▶
                    </span>
                    <span className="source-count">{duplicate.count} sources</span>
                    {duplicate.seoAnalysis && (
                      <SEOScore 
                        score={duplicate.seoAnalysis.averageScore}
                        grade={duplicate.seoAnalysis.grade}
                        compact={true}
                      />
                    )}
                    <span className="target-label">Target:</span>
                    <code className="target-url">{duplicate.target}</code>
                  </div>
                </div>
                
                {expandedGroups.has(index) && (
                  <div className="sources-list">
                    {duplicate.seoAnalysis && (
                      <div className="seo-analysis-section">
                        <SEOScore 
                          score={duplicate.seoAnalysis.averageScore}
                          grade={duplicate.seoAnalysis.grade}
                          issues={duplicate.seoAnalysis.sourceAnalyses[0]?.issues || []}
                          recommendations={duplicate.seoAnalysis.recommendations}
                          showDetails={false}
                        />
                        {duplicate.seoAnalysis.worstScore !== duplicate.seoAnalysis.bestScore && (
                          <div className="score-range">
                            <small>
                              Score range: {duplicate.seoAnalysis.worstScore} - {duplicate.seoAnalysis.bestScore}
                              {duplicate.seoAnalysis.commonIssues.length > 0 && (
                                <span> • Common issues found</span>
                              )}
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {duplicate.sources.map((source, sourceIndex) => (
                      <div key={sourceIndex} className="source-item">
                        <div className="source-number">{sourceIndex + 1}</div>
                        <div className="source-content">
                          <div className="source-url">
                            <strong>Source:</strong>
                            <code>{source.source}</code>
                          </div>
                          <div className="source-meta">
                            {duplicate.seoAnalysis?.sourceAnalyses[sourceIndex] && (
                              <SEOScore 
                                score={duplicate.seoAnalysis.sourceAnalyses[sourceIndex].score}
                                grade={duplicate.seoAnalysis.sourceAnalyses[sourceIndex].grade}
                                compact={true}
                              />
                            )}
                            <span 
                              className="status-badge"
                              style={{ backgroundColor: getStatusColor(source.status) }}
                            >
                              {source.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DuplicateRedirects; 